<template>
  <div class="c-history-table">
    <table v-if="logs.length">
      <thead>
        <tr>
          <th class="c-history-table__col--action">
            Action
          </th>
          <th>Title</th>
          <th class="c-history-table__col--user">
            User
          </th>
          <th class="c-history-table__col--when">
            When
          </th>
          <th class="c-history-table__col--open" />
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(log, index) in logs"
          :key="log.id || index"
          tabindex="0"
          @click="$emit('open', log)"
          @keydown.enter="$emit('open', log)"
        >
          <td>
            <span :class="['c-history-badge', `c-history-badge--${action(log).tone}`]">
              <app-icon
                :name="action(log).icon"
                :size="14"
              />
              {{ action(log).label }}
            </span>
          </td>
          <td class="c-history-table__title">
            <!-- VIP entries may only carry a message -->
            {{ log.title || (log.description && log.description.message) }}
          </td>
          <td>
            <span class="c-history-table__user">
              <span class="c-history-table__avatar">{{ initials(log) }}</span>
              <span class="c-history-table__email">{{ log.user && log.user.email }}</span>
            </span>
          </td>
          <td>
            <span
              class="c-history-table__when"
              :title="dateTime(log)"
            >
              <span>{{ relative(log) }}</span>
              <small>{{ dateTime(log) }}</small>
            </span>
          </td>
          <td class="c-history-table__open">
            <span class="c-history-table__open-btn">
              Details
              <app-icon
                name="chevron_right"
                :size="18"
              />
            </span>
          </td>
        </tr>
      </tbody>
    </table>

    <div
      v-else
      class="c-table__empty"
    >
      <span class="c-table__empty-icon">
        <app-icon
          name="history"
          :size="24"
        />
      </span>
      <p class="c-table__empty-title">
        No history yet
      </p>
      <p>Changes will appear here as they are made.</p>
    </div>
  </div>
</template>

<script>
import AppIcon from '@/shared/app-icon/AppIcon';
import { getHistoryLogAction, getRelativeTime, formatDateTime, getInitials } from './historyLogActions';

// Table of history log entries; emits `open` with the entry when a row is clicked.
export default {
  name: 'HistoryLogTable',
  components: {
    AppIcon,
  },
  props: {
    logs: {
      type: Array,
      required: true,
    },
  },
  methods: {
    action(log) {
      return getHistoryLogAction(log);
    },
    relative(log) {
      return getRelativeTime(log.createdAt);
    },
    dateTime(log) {
      return formatDateTime(log.createdAt);
    },
    initials(log) {
      return getInitials(log.user && log.user.email);
    },
  },
};
</script>

<style lang="scss">
// ---------- action badge (also used by the details modal) ----------
.c-history-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 24px;
  padding: 0 10px 0 8px;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-full);
  background-color: var(--bg-subtle);
  color: var(--text-secondary);
  font-family: var(--font-sans);
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;

  &--success {
    border-color: var(--success-border);
    background-color: var(--success-bg);
    color: var(--success-fg);
  }

  &--info {
    border-color: var(--info-border);
    background-color: var(--info-bg);
    color: var(--info-fg);
  }

  &--danger {
    border-color: var(--danger-border);
    background-color: var(--danger-bg);
    color: var(--danger-fg);
  }

  &--accent {
    border-color: var(--brand-200);
    background-color: var(--brand-50);
    color: var(--brand-fg);
  }
}
</style>

<style lang="scss" scoped>
.c-history-table {
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  background-color: var(--bg-surface);
  font-family: var(--font-sans);
  overflow: hidden;

  table {
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
  }

  th,
  td {
    padding: 12px 16px;
    text-align: left;
    vertical-align: middle;

    &:first-child {
      padding-left: 24px;
    }

    &:last-child {
      padding-right: 24px;
    }
  }

  th {
    height: 44px;
    padding-top: 0;
    padding-bottom: 0;
    background-color: var(--bg-subtle);
    border-bottom: 1px solid var(--border-default);
    color: var(--text-secondary);
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  td {
    border-bottom: 1px solid var(--border-default);
    font-size: 14px;
    color: var(--text-primary);
  }

  tbody tr {
    cursor: pointer;
    transition: background-color var(--duration-fast) ease;

    &:last-child td {
      border-bottom: none;
    }

    &:hover,
    &:focus-visible {
      background-color: var(--bg-subtle);
      outline: none;

      .c-history-table__open-btn {
        color: var(--text-primary);
      }
    }
  }
}

.c-history-table__col--action {
  width: 140px;
}

.c-history-table__col--user {
  width: 22%;
}

.c-history-table__col--when {
  width: 160px;
}

.c-history-table__col--open {
  width: 104px;
}

.c-history-table__title {
  overflow: hidden;
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.c-history-table__user {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.c-history-table__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: var(--bg-hover);
  color: var(--text-secondary);
  font-size: 11px;
  font-weight: 600;
}

.c-history-table__email {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.c-history-table__when {
  display: flex;
  flex-direction: column;
  gap: 2px;
  white-space: nowrap;

  span {
    font-weight: 500;
  }

  small {
    font-size: 12px;
    color: var(--text-muted);
  }
}

.c-history-table__open {
  text-align: right !important;
}

.c-history-table__open-btn {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 13px;
  font-weight: 500;
  color: var(--text-muted);
  transition: color var(--duration-fast) ease;
}
</style>
